/**
 * LiveMeetingPane - right-rail panel for Zoom RTMS live meeting intelligence.
 *
 * When a dealUuid is provided, the pane gains a second "Questions" tab that
 * surfaces AI-powered discovery coaching:
 *
 *  1. On mount, fetches all questionnaire fields (gate + ai_enabled) for the deal.
 *  2. As transcript chunks arrive (debounced 2 s), calls the coaching endpoint
 *     with the last 10 final chunks and speaker-role context.
 *  3. Renders each field with source-aware styling:
 *       pending   → grey, waiting
 *       gate      → label-only context marker (parent field, not tracked)
 *       suggested → blue highlight with AI-generated question text
 *       answered (db)   → muted grey - pre-existing deal answer
 *       answered (live) → green with "New" badge - captured this session
 *  4. When the session ends, all live-captured answers are saved to the deal
 *     via PUT /api/sales/deals/:dealUuid/questionnaire. DB answers are never
 *     overwritten - only fields with no prior value or that received a new live
 *     answer are written.
 */

import { Tooltip } from '@protoss/ui';
import * as React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { salesApi } from '../../sales/services/salesApi';
import { useLiveMeetingSession } from '../hooks/useLiveMeetingSession';
import type {
  DealCoachField,
  FieldCoachingState,
  LiveMeetingStatus,
  MeetingRecommendation,
  TranscriptSegment,
} from '../types/live-meeting';
import './live-meeting-pane.css';

// ── Types ─────────────────────────────────────────────────────────────────

type CoachEvent = {
  id: string;
  kind: 'suggestion' | 'captured';
  fieldKey: string;
  label: string;
  content: string;
  afterChunkIndex: number;
};

type FeedItem =
  | { kind: 'chunk'; data: TranscriptSegment; key: string }
  | { kind: 'rec'; data: MeetingRecommendation; recKey: string }
  | { kind: 'coach-suggestion'; id: string; label: string; question: string }
  | { kind: 'coach-captured'; id: string; label: string; answer: string };

type PaneTab = 'transcript' | 'questions';

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * Build the interleaved feed: recommendation cards are inserted immediately
 * after the transcript segment whose text matches rec.triggerText.
 * Unanchored recs (no matching segment yet) are appended at the end.
 * System event segments (participant joined/left) are filtered out of the feed.
 */
function buildFeed(
  segments: TranscriptSegment[],
  recs: MeetingRecommendation[],
  coachEvents: CoachEvent[]
): FeedItem[] {
  // Only show speech segments in this compact pane (system events are omitted)
  const chunks = segments.filter((s) => !s.isSystemEvent);

  const anchorMap = new Map<number, MeetingRecommendation[]>();
  const unanchored: MeetingRecommendation[] = [];

  for (const rec of recs) {
    let anchorIdx = -1;
    for (let j = chunks.length - 1; j >= 0; j--) {
      if (chunks[j]?.text === rec.triggerText) {
        anchorIdx = j;
        break;
      }
    }
    if (anchorIdx !== -1) {
      const arr = anchorMap.get(anchorIdx) ?? [];
      arr.push(rec);
      anchorMap.set(anchorIdx, arr);
    } else {
      unanchored.push(rec);
    }
  }

  const coachAnchorMap = new Map<number, CoachEvent[]>();
  const unanchoredCoach: CoachEvent[] = [];
  for (const event of coachEvents) {
    const idx = event.afterChunkIndex;
    if (idx >= 0 && idx < chunks.length) {
      const arr = coachAnchorMap.get(idx) ?? [];
      arr.push(event);
      coachAnchorMap.set(idx, arr);
    } else {
      unanchoredCoach.push(event);
    }
  }

  const items: FeedItem[] = [];
  for (let i = 0; i < chunks.length; i++) {
    items.push({ kind: 'chunk', data: chunks[i]!, key: `chunk-${i}` });
    const afterThis = anchorMap.get(i);
    if (afterThis) {
      for (const rec of afterThis) {
        items.push({ kind: 'rec', data: rec, recKey: `${rec.ruleKey}-${rec.timestamp}` });
      }
    }
    const afterThisCoach = coachAnchorMap.get(i);
    if (afterThisCoach) {
      for (const ev of afterThisCoach) {
        if (ev.kind === 'suggestion') {
          items.push({
            kind: 'coach-suggestion',
            id: ev.id,
            label: ev.label,
            question: ev.content,
          });
        } else {
          items.push({ kind: 'coach-captured', id: ev.id, label: ev.label, answer: ev.content });
        }
      }
    }
  }
  for (const rec of unanchored) {
    items.push({ kind: 'rec', data: rec, recKey: `${rec.ruleKey}-${rec.timestamp}` });
  }
  for (const ev of unanchoredCoach) {
    if (ev.kind === 'suggestion') {
      items.push({ kind: 'coach-suggestion', id: ev.id, label: ev.label, question: ev.content });
    } else {
      items.push({ kind: 'coach-captured', id: ev.id, label: ev.label, answer: ev.content });
    }
  }

  return items;
}

// ── RTMS Debug Panel ──────────────────────────────────────────────────────

/**
 * Temporary dev panel - shows the latest raw WS payload for every RTMS event
 * type received so far. Helps inspect what fields Zoom/API actually sends.
 */
function RtmsDebugPanel({ rawEventsByType }: { rawEventsByType: Record<string, unknown> }) {
  const [open, setOpen] = React.useState(false);
  const entries = Object.entries(rawEventsByType);

  return (
    <div className="lm-pane__debug">
      <button
        type="button"
        className="lm-pane__debug-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>
          RTMS debug ({entries.length} event type{entries.length !== 1 ? 's' : ''})
        </span>
      </button>
      {open && (
        <div className="lm-pane__debug-body">
          {entries.length === 0 ? (
            <p className="lm-pane__debug-empty">No events received yet.</p>
          ) : (
            entries.map(([type, payload]) => (
              <div key={type} className="lm-pane__debug-entry">
                <div className="lm-pane__debug-entry-type">{type}</div>
                <pre className="lm-pane__debug-pre">{JSON.stringify(payload, null, 2)}</pre>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Status bar ────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<LiveMeetingStatus, string> = {
  disconnected: 'Disconnected',
  connecting: 'Connecting…',
  waiting: 'Waiting for meeting',
  active: 'Meeting active',
  ended: 'Meeting ended',
  error: 'Connection error',
};

function StatusBar({ status }: { status: LiveMeetingStatus }) {
  return (
    <div className="lm-pane__status-bar">
      <span className={`lm-pane__dot lm-pane__dot--${status}`} aria-hidden="true" />
      <span className="lm-pane__status-label">{STATUS_LABELS[status]}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export function LiveMeetingPane({ dealUuid }: { dealUuid?: string }) {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const session = useLiveMeetingSession(workspace?.uuid);

  // ── Tab state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = React.useState<PaneTab>('transcript');
  // Badge: count of newly-suggested questions since user last viewed Questions tab
  const [newSuggestions, setNewSuggestions] = React.useState(0);

  // ── Deal coaching state ───────────────────────────────────────────────────
  const [dealFields, setDealFields] = React.useState<DealCoachField[]>([]);
  const [fieldStates, setFieldStates] = React.useState<Record<string, FieldCoachingState>>({});
  const coachingTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCoachChunkCountRef = React.useRef(0);
  // Fields that have been suggested to the rep but not yet answered.
  // The field stays in the unanswered list so the AI can detect when the prospect answers it.
  const suggestedFieldKeysRef = React.useRef<Set<string>>(new Set());
  const isCoachingRef = React.useRef(false);

  // ── Recommendation state (transcript tab) ────────────────────────────────
  const [chosenRecs, setChosenRecs] = React.useState<Record<string, string>>({});
  const [coachEvents, setCoachEvents] = React.useState<CoachEvent[]>([]);

  const transcriptScrollRef = React.useRef<HTMLDivElement>(null);
  // Always-current ref so stale setTimeout callbacks can read the live segment count.
  const segmentsLengthRef = React.useRef(session.segments.length);
  segmentsLengthRef.current = session.segments.length;

  // Load deal ai_enabled fields when dealUuid is available
  React.useEffect(() => {
    if (!dealUuid) return;
    let cancelled = false;
    salesApi
      .getDealQuestionnaire(dealUuid)
      .then(({ questionnaire }) => {
        if (cancelled) return;
        const fields: DealCoachField[] = [];
        for (const section of questionnaire.sections) {
          for (const question of section.groups) {
            for (const field of question.fields) {
              if (field.aiEnabled) {
                fields.push({
                  fieldKey: field.fieldKey,
                  label: field.label,
                  description: field.description,
                  aiPrompt: field.aiPrompt,
                  fieldType: field.fieldType,
                  options: field.options.map((o) => ({ label: o.label, value: o.value })),
                  currentValue: field.value ?? undefined,
                });
              }
            }
          }
        }
        setDealFields(fields);
        // Initialise all fields as pending
        const initial: Record<string, FieldCoachingState> = {};
        for (const f of fields) {
          initial[f.fieldKey] = { status: 'pending' };
        }
        setFieldStates(initial);
      })
      .catch(() => {
        // silently ignore - coaching is best-effort
      });
    return () => {
      cancelled = true;
    };
  }, [dealUuid]);

  // Reset coaching state when session restarts
  React.useEffect(() => {
    if (session.status === 'active') {
      suggestedFieldKeysRef.current = new Set();
      lastCoachChunkCountRef.current = 0;
      setNewSuggestions(0);
      setCoachEvents([]);
      setFieldStates((prev) => {
        const reset: Record<string, FieldCoachingState> = {};
        for (const key of Object.keys(prev)) {
          reset[key] = { status: 'pending' };
        }
        return reset;
      });
    }
  }, [session.status]);

  // Debounced coaching trigger: fires 2 s after last new final chunk
  React.useEffect(() => {
    if (!dealUuid || dealFields.length === 0) return;
    if (session.status !== 'active') return;

    const newFinalCount = session.transcriptChunks.length;
    if (newFinalCount <= lastCoachChunkCountRef.current) return;

    if (coachingTimerRef.current) clearTimeout(coachingTimerRef.current);

    coachingTimerRef.current = setTimeout(() => {
      if (isCoachingRef.current) return;
      isCoachingRef.current = true;

      const chunks = session.transcriptChunks;
      lastCoachChunkCountRef.current = chunks.length;
      const recentChunks = chunks.slice(-10).map((c) => ({ userName: c.userName, text: c.text }));

      salesApi
        .extractLiveMeeting(dealUuid, {
          recentChunks,
          suggestedFieldKeys: Array.from(suggestedFieldKeysRef.current),
          hostName: user?.displayName ?? null,
        })
        .then(({ results }) => {
          const stateUpdates: Record<string, FieldCoachingState> = {};
          for (const result of results) {
            if (result.type === 'question_suggestion') {
              const label =
                dealFields.find((f) => f.fieldKey === result.fieldKey)?.label ?? result.fieldKey;
              suggestedFieldKeysRef.current.add(result.fieldKey);
              stateUpdates[result.fieldKey] = {
                status: 'suggested',
                suggestedQuestion: result.suggestedQuestion,
              };
              setCoachEvents((prev) => [
                ...prev,
                {
                  id: `suggestion-${result.fieldKey}-${Date.now()}`,
                  kind: 'suggestion' as const,
                  fieldKey: result.fieldKey,
                  label,
                  content: result.suggestedQuestion,
                  afterChunkIndex: segmentsLengthRef.current - 1,
                },
              ]);
              setNewSuggestions((n) => n + 1);
            } else if (result.type === 'answer_extracted') {
              const label =
                dealFields.find((f) => f.fieldKey === result.fieldKey)?.label ?? result.fieldKey;
              suggestedFieldKeysRef.current.delete(result.fieldKey);
              stateUpdates[result.fieldKey] = {
                status: 'answered',
                answer: result.answer,
                source: 'live',
              };
              setCoachEvents((prev) => [
                ...prev,
                {
                  id: `captured-${result.fieldKey}-${Date.now()}`,
                  kind: 'captured' as const,
                  fieldKey: result.fieldKey,
                  label,
                  content: result.answer,
                  afterChunkIndex: chunks.length - 1,
                },
              ]);
            }
          }
          if (Object.keys(stateUpdates).length > 0) {
            setFieldStates((prev) => ({ ...prev, ...stateUpdates }));
          }
        })
        .catch((err: unknown) => {
          console.error('[LiveMeetingPane] coaching API error', err);
        })
        .finally(() => {
          isCoachingRef.current = false;
        });
    }, 900);

    return () => {
      if (coachingTimerRef.current) clearTimeout(coachingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.transcriptChunks.length, session.status, dealUuid, dealFields.length]);

  // Auto-scroll transcript to bottom when new content arrives
  React.useEffect(() => {
    if (activeTab !== 'transcript') return;
    const el = transcriptScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [session.segments.length, session.recommendations.length, coachEvents.length, activeTab]);

  // Clear badge when user switches to questions tab
  const handleTabChange = (tab: PaneTab) => {
    setActiveTab(tab);
    if (tab === 'questions') setNewSuggestions(0);
  };

  const hasContent = session.segments.length > 0 || session.recommendations.length > 0;

  const feed = React.useMemo(
    () => buildFeed(session.segments, session.recommendations, coachEvents),
    [session.segments, session.recommendations, coachEvents]
  );

  const hasDealFields = dealFields.length > 0;
  const answeredCount = Object.values(fieldStates).filter((s) => s.status === 'answered').length;

  return (
    <div className="lm-pane">
      <StatusBar status={session.status} />

      {/* Tab bar - only shown when deal fields are loaded */}
      {hasDealFields && (
        <div className="lm-pane__tabs" role="tablist">
          <button
            role="tab"
            type="button"
            aria-selected={activeTab === 'transcript'}
            className={`lm-pane__tab${activeTab === 'transcript' ? ' lm-pane__tab--active' : ''}`}
            onClick={() => handleTabChange('transcript')}
          >
            Transcript
          </button>
          <button
            role="tab"
            type="button"
            aria-selected={activeTab === 'questions'}
            className={`lm-pane__tab${activeTab === 'questions' ? ' lm-pane__tab--active' : ''}`}
            onClick={() => handleTabChange('questions')}
          >
            Questions
            {answeredCount > 0 && (
              <span className="lm-pane__tab-badge lm-pane__tab-badge--answered">
                {answeredCount}/{dealFields.length}
              </span>
            )}
            {newSuggestions > 0 && activeTab !== 'questions' && (
              <span className="lm-pane__tab-badge lm-pane__tab-badge--new">{newSuggestions}</span>
            )}
          </button>
        </div>
      )}

      {session.errorMessage && session.status === 'error' ? (
        <div className="lm-pane__empty">
          <span className="lm-pane__empty-title">Connection error</span>
          <span>{session.errorMessage}</span>
        </div>
      ) : activeTab === 'questions' && hasDealFields ? (
        <QuestionsPanel fields={dealFields} fieldStates={fieldStates} />
      ) : !hasContent && session.status !== 'active' && session.status !== 'ended' ? (
        <div className="lm-pane__empty">
          <span className="lm-pane__empty-title">No active meeting</span>
          <span>
            Protoss will surface recommendations here once a Zoom meeting starts in your workspace.
          </span>
        </div>
      ) : (
        <div className="lm-pane__transcript">
          <div className="lm-pane__transcript-heading">Transcript</div>
          <div className="lm-pane__transcript-scroll" ref={transcriptScrollRef}>
            {feed.map((item) => {
              if (item.kind === 'chunk') {
                const seg = item.data;
                return (
                  <div
                    key={item.key}
                    className="lm-pane__chunk"
                    data-interim={seg.status === 'finalized' ? undefined : true}
                  >
                    <span className="lm-pane__chunk-speaker">
                      {seg.speakerName}
                      {seg.speakerRole && (
                        <span className="lm-pane__chunk-role">{seg.speakerRole}</span>
                      )}
                      <Tooltip.Simple
                        label={`Provider→API: ${seg.providerToApiMs}ms | API→UI: ${seg.apiToUiMs}ms`}
                      >
                        <span className="lm-pane__chunk-latency">
                          +{(seg.sessionOffsetMs / 1000).toFixed(1)}s
                        </span>
                      </Tooltip.Simple>
                    </span>
                    <span className="lm-pane__chunk-text">{seg.text}</span>
                  </div>
                );
              }

              // Coach events
              if (item.kind === 'coach-suggestion') {
                return (
                  <div
                    key={item.id}
                    className="lm-pane__coach-event lm-pane__coach-event--suggestion"
                  >
                    <div className="lm-pane__coach-event-label">
                      <span className="lm-pane__coach-event-icon" aria-hidden="true">
                        💡
                      </span>
                      <span>
                        Ask: <strong>{item.label}</strong>
                      </span>
                    </div>
                    <div className="lm-pane__coach-event-content">{item.question}</div>
                  </div>
                );
              }
              if (item.kind === 'coach-captured') {
                return (
                  <div
                    key={item.id}
                    className="lm-pane__coach-event lm-pane__coach-event--captured"
                  >
                    <div className="lm-pane__coach-event-label">
                      <span className="lm-pane__coach-event-icon" aria-hidden="true">
                        ✓
                      </span>
                      <span>
                        Captured: <strong>{item.label}</strong>
                      </span>
                    </div>
                    <div className="lm-pane__coach-event-content">{item.answer}</div>
                  </div>
                );
              }

              // Recommendation card
              const { data: rec, recKey } = item;
              const chosen = chosenRecs[recKey];

              if (chosen) {
                return (
                  <div key={recKey} className="lm-pane__rec-chosen">
                    <span className="lm-pane__rec-chosen-check" aria-hidden="true">
                      ✓
                    </span>
                    <span className="lm-pane__rec-chosen-text">{chosen}</span>
                  </div>
                );
              }

              return (
                <div key={recKey} className="lm-pane__rec-card">
                  <span className="lm-pane__rec-trigger">{rec.triggerTerm}</span>
                  {rec.triggerText && (
                    <Tooltip.Simple label="Triggering transcript line">
                      <span className="lm-pane__rec-trigger-text">
                        &ldquo;{rec.triggerText}&rdquo;
                      </span>
                    </Tooltip.Simple>
                  )}
                  <ul className="lm-pane__rec-suggestions">
                    {rec.suggestions.map((suggestion, si) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={si} className="lm-pane__rec-suggestion">
                        <button
                          type="button"
                          className="lm-pane__rec-suggestion-btn"
                          onClick={() =>
                            setChosenRecs((prev) => ({ ...prev, [recKey]: suggestion }))
                          }
                        >
                          {suggestion}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <RtmsDebugPanel rawEventsByType={session.rawEventsByType} />
    </div>
  );
}

// ── Questions panel ───────────────────────────────────────────────────────

function QuestionsPanel({
  fields,
  fieldStates,
}: {
  fields: DealCoachField[];
  fieldStates: Record<string, FieldCoachingState>;
}) {
  const pending = fields.filter((f) => fieldStates[f.fieldKey]?.status === 'pending');
  const suggested = fields.filter((f) => fieldStates[f.fieldKey]?.status === 'suggested');
  const answered = fields.filter((f) => fieldStates[f.fieldKey]?.status === 'answered');

  const totalCount = fields.length;
  const answeredCount = answered.length;
  const progressPct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;

  // "Up next": current AI suggestions first, then fill to 3 with first pending fields
  const upNextPending = pending.slice(0, Math.max(0, 3 - suggested.length));
  const remainingPending = pending.slice(upNextPending.length);
  const upNext = [...suggested, ...upNextPending];

  return (
    <div className="lm-pane__questions">
      <div className="lm-pane__questions-scroll">
        {totalCount > 0 && (
          <div className="lm-pane__progress">
            <div className="lm-pane__progress-header">
              <span className="lm-pane__progress-label">Discovery progress</span>
              <span className="lm-pane__progress-count">
                {answeredCount} / {totalCount}
              </span>
            </div>
            <div
              className="lm-pane__progress-bar"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="lm-pane__progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {upNext.length > 0 && (
          <section className="lm-pane__q-section">
            <div className="lm-pane__q-section-label">Up next</div>
            {upNext.map((f) => (
              <FieldCard key={f.fieldKey} field={f} state={fieldStates[f.fieldKey]!} showHint />
            ))}
          </section>
        )}

        {answered.length > 0 && (
          <section className="lm-pane__q-section">
            <div className="lm-pane__q-section-label">Captured ({answeredCount})</div>
            {answered.map((f) => (
              <FieldCard key={f.fieldKey} field={f} state={fieldStates[f.fieldKey]!} />
            ))}
          </section>
        )}

        {remainingPending.length > 0 && (
          <section className="lm-pane__q-section">
            <div className="lm-pane__q-section-label">Pending ({remainingPending.length})</div>
            {remainingPending.map((f) => (
              <FieldCard key={f.fieldKey} field={f} state={fieldStates[f.fieldKey]!} />
            ))}
          </section>
        )}

        {fields.length === 0 && (
          <div className="lm-pane__empty">
            <span className="lm-pane__empty-title">No discovery fields</span>
            <span>Enable AI on deal template fields to see coaching here.</span>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldCard({
  field,
  state,
  showHint = false,
}: {
  field: DealCoachField;
  state: FieldCoachingState;
  showHint?: boolean;
}) {
  const handleCopy = () => {
    if (state.status === 'suggested' && state.suggestedQuestion) {
      void navigator.clipboard.writeText(state.suggestedQuestion);
    }
  };

  const hint = field.description ?? null;

  return (
    <div
      className={`lm-pane__field-card lm-pane__field-card--${state.status}`}
      aria-label={`${field.label}: ${state.status}`}
    >
      <div className="lm-pane__field-header">
        <span className="lm-pane__field-status-dot" aria-hidden="true" />
        <span className="lm-pane__field-label">{field.label}</span>
      </div>

      {showHint && state.status === 'pending' && hint && (
        <div className="lm-pane__field-hint">{hint}</div>
      )}

      {state.status === 'suggested' && state.suggestedQuestion && (
        <div className="lm-pane__field-suggestion">
          <span className="lm-pane__field-suggestion-text">{state.suggestedQuestion}</span>
          <Tooltip.Simple label="Copy to clipboard">
            <button
              type="button"
              className="lm-pane__field-copy-btn"
              onClick={handleCopy}
              aria-label="Copy suggested question"
            >
              Copy
            </button>
          </Tooltip.Simple>
        </div>
      )}

      {state.status === 'answered' && state.answer && (
        <div className="lm-pane__field-answer">
          <span className="lm-pane__field-answer-text">{state.answer}</span>
        </div>
      )}
    </div>
  );
}
